import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Copy, FileCode } from 'lucide-react';
import { toast } from 'sonner';
import SettingsModal from '../../Settings/SettingsModal';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { apiError, createFile, downloadFileContent, fetchDebtorAccounts, previewFile, previewFileXml } from './paymentsApi';
import { CATEGORY, SCHEME_LABEL, kr } from './paymentStatus';

/**
 * "Skapa betalfil" — pick the debtor account and date, see the re-derived
 * rows, then create and lock the file in one step. Preview is refreshed on
 * every change; nothing is written until "Skapa och lås fil".
 */
const AssembleFileDialog = ({ show, companyId, instructionIds, onClose, onCreated }) => {
  const [accounts, setAccounts] = useState(null);
  const [mappingId, setMappingId] = useState('');
  const [date, setDate] = useState('');
  const [dateTouched, setDateTouched] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [xml, setXml] = useState(null);
  const [xmlLoading, setXmlLoading] = useState(false);
  // Only the newest preview request may write its answer into the dialog.
  const previewSeq = useRef(0);

  useEffect(() => {
    if (!show) return;
    setPreview(null); setError(null); setDate(''); setDateTouched(false);
    fetchDebtorAccounts(companyId)
      .then((list) => {
        setAccounts(list);
        if (list.length === 1) setMappingId(list[0].mapping_id);
      })
      .catch((err) => toast.error(apiError(err, 'Kunde inte hämta bankkonton.').message));
  }, [show, companyId]);

  // The date is sent only once the user has picked one; the proposed date the
  // preview fills in must not trigger a second preview.
  const requestedDate = dateTouched && date ? date : undefined;

  const runPreview = useCallback(async () => {
    const mine = ++previewSeq.current; // also drops an answer still on its way when the dialog closes
    if (!show || !mappingId || !instructionIds.length) return;
    setLoading(true);
    setError(null);
    setXml(null);
    try {
      const p = await previewFile(companyId, { instructionIds, debtorMappingId: mappingId, requestedExecutionDate: requestedDate });
      if (mine !== previewSeq.current) return;
      setPreview(p);
      if (!dateTouched && p.executionDate) setDate(p.executionDate);
    } catch (err) {
      if (mine !== previewSeq.current) return;
      setError(apiError(err, 'Förhandsgranskningen misslyckades.'));
      setPreview(null);
    } finally {
      if (mine === previewSeq.current) setLoading(false);
    }
  }, [show, companyId, instructionIds, mappingId, requestedDate, dateTouched]);

  useEffect(() => { runPreview(); }, [runPreview]);

  const options = useMemo(() => (accounts || []).map((a) => ({ value: a.mapping_id, label: `${a.display}${a.iban ? ` · ${a.iban}` : ''}` })), [accounts]);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const result = await createFile(companyId, { instructionIds, debtorMappingId: mappingId, requestedExecutionDate: date || undefined });
      try {
        await downloadFileContent(companyId, result.file);
      } catch (dlErr) {
        toast.error('Filen är skapad men nedladdningen misslyckades. Ladda ner den under Betalfiler.');
      }
      toast.success(`Betalfilen "${result.file.label}" är skapad och låst.`);
      onCreated(result.file);
    } catch (err) {
      const e = apiError(err, 'Betalfilen kunde inte skapas.');
      setError(e);
      if (e.details?.rows?.length || e.details?.blocking?.length) runPreview();
    } finally {
      setCreating(false);
    }
  };

  const showXml = async () => {
    if (xml) { setXml(null); return; }
    setXmlLoading(true);
    try {
      const result = await previewFileXml(companyId, { instructionIds, debtorMappingId: mappingId, requestedExecutionDate: date || undefined });
      if (!result.xml) { toast.error('Filen kan inte förhandsgranskas förrän alla rader är giltiga.'); return; }
      setXml(result.xml);
    } catch (err) {
      toast.error(apiError(err, 'Förhandsgranskningen av filen misslyckades.').message);
    } finally {
      setXmlLoading(false);
    }
  };

  const copyXml = async () => {
    try {
      await navigator.clipboard.writeText(xml);
      toast.success('XML kopierad.');
    } catch {
      toast.error('Kunde inte kopiera.');
    }
  };

  const footer = (
    <>
      <button type="button" className="ll-btn ll-btn--secondary" onClick={onClose} disabled={creating}>Avbryt</button>
      <button type="button" className="ll-btn ll-btn--secondary" onClick={showXml} disabled={!preview?.canAssemble || creating || loading || xmlLoading} title="Visa filen som den kommer att skrivas, utan att skapa den">
        <FileCode size={14} className="me-1" />{xmlLoading ? 'Hämtar…' : xml ? 'Dölj XML' : 'Visa XML'}
      </button>
      <button type="button" className="ll-btn ll-btn--primary" onClick={create} disabled={!preview?.canAssemble || creating || loading}>
        {creating ? 'Skapar…' : 'Skapa och lås fil'}
      </button>
    </>
  );

  return (
    <SettingsModal show={show} onHide={onClose} title="Skapa betalfil" size="lg" footer={footer}>
      {accounts && accounts.length === 0 && (
        <div className="alert alert-warning" style={{ fontSize: 13 }}>
          Inget bankkonto är aktiverat för betalfiler.{' '}
          <Link to={`/${companyId}/settings/bank-connections`}>Öppna Inställningar → Bankkonton</Link> och fyll i bank och avtalsuppgifter.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
        <Form.Group>
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Dras från konto</Form.Label>
          <Select
            options={options}
            value={options.find((o) => o.value === mappingId) || null}
            onChange={(o) => setMappingId(o ? o.value : '')}
            placeholder={accounts ? 'Välj bankkonto…' : 'Laddar…'}
            theme={brandSelectTheme}
            isDisabled={!accounts || accounts.length === 0}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
            {preview?.categoryPurpose === 'SALA' ? 'Dras från kontot' : 'Betalningsdatum'}
          </Form.Label>
          <Form.Control type="date" value={date} onChange={(e) => { setDate(e.target.value); setDateTouched(true); }} disabled={!mappingId} />
          {preview?.categoryPurpose === 'SALA' && preview.payoutDate && (
            <div className="text-muted" style={{ fontSize: 12 }}>Utbetalas {preview.payoutDate}</div>
          )}
        </Form.Group>
      </div>

      {preview?.profile && (
        <div style={{ fontSize: 12, color: '#475569', background: '#f8fafc', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
          <strong>{preview.profile.name}:</strong> {preview.profile.uploadChannel}
        </div>
      )}

      {error && (
        <div className="alert alert-danger" style={{ fontSize: 13 }}>
          <AlertCircle size={14} className="me-1" /> {error.message}
          {Array.isArray(error.details?.blocking) && error.details.blocking.map((b) => <div key={b}>• {b}</div>)}
        </div>
      )}

      {preview?.blocking?.length > 0 && (
        <div className="alert alert-danger" style={{ fontSize: 13 }}>
          {preview.blocking.map((b) => <div key={b}><AlertCircle size={14} className="me-1" />{b}</div>)}
        </div>
      )}

      {loading && !preview ? (
        <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
      ) : preview ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
            <span>{CATEGORY[preview.categoryPurpose] || preview.categoryPurpose} · {preview.count} betalningar</span>
            <strong>{kr(preview.totalAmount)}</strong>
          </div>
          <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr><th>Mottagare</th><th>Konto</th><th className="text-end">Belopp</th><th>Status</th></tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.instruction_id} style={r.errors.length ? { background: '#fef2f2' } : undefined}>
                    <td>{r.label}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {r.creditor ? `${SCHEME_LABEL[r.creditor.scheme] || r.creditor.scheme} ${r.creditor.clearing ? `${r.creditor.clearing}-` : ''}${r.creditor.masked}` : '—'}
                    </td>
                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{kr(r.amount)}</td>
                    <td>
                      {r.errors.map((e) => <div key={e} className="text-danger"><AlertCircle size={12} className="me-1" />{e}</div>)}
                      {r.warnings.map((w) => <div key={w} style={{ color: '#b45309' }}><AlertTriangle size={12} className="me-1" />{w}</div>)}
                      {!r.errors.length && !r.warnings.length && <span className="text-muted">OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.rowErrorCount > 0 && (
            <div className="text-danger" style={{ fontSize: 13, marginTop: 8 }}>
              {preview.rowErrorCount} betalning{preview.rowErrorCount === 1 ? '' : 'ar'} kan inte tas med. Rätta uppgifterna och öppna dialogen igen.
            </div>
          )}
          {xml && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, marginBottom: 6 }}>
                <span>
                  <strong>Förhandsgranskning av filen</strong>{' '}
                  <span className="text-muted">— pain.001.001.03, meddelande-id börjar med PV och är inte den fil som skapas.</span>
                </span>
                <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={copyXml}>
                  <Copy size={12} className="me-1" />Kopiera
                </button>
              </div>
              <pre style={{ maxHeight: 360, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, fontSize: 12, lineHeight: 1.4, background: '#f9fafb', margin: 0 }}>
                {xml}
              </pre>
            </div>
          )}
        </>
      ) : null}
    </SettingsModal>
  );
};

export default AssembleFileDialog;
